import { connect } from 'react-redux';
import compose from 'recompose/compose';
import {
  HotelSegmentDetails,
  OptionId,
} from '../../../../../../../models/itinerary';
import { AppState } from '../../../../../../../store/model';
import {
  getProviderNames,
  getSegmentOption,
} from '../../../../../../../store/reducers/segment-options/selectors';
import { commaSeparated } from '../../../../../../../utils/helpers';
import { HotelProviderDetails } from '../../../../../../lab/ProviderDetails/HotelProviderDetails';
import { isSaviSelectIntl } from '../../../../../../../store/reducers/itinerary/selectors';
import { getAHotelDetailsForSaviSelectIntl } from '../../../../../../../store/reducers/itinerary/saviSelectIntlSelector';

interface OwnProps {
  optionId: OptionId;
  segmentId: number;
}

interface StateProps {
  /**
   * Address for the hotel
   *
   * @example
   * "1 Hobson St Downtown, North Island, New Zealand"
   */
  address: string;

  /**
   * Name of the hotel provider
   *
   * @example
   * "Vantis Hotel Group"
   */
  providerName: string;

  /**
   * Star rating out of 5 for the provider
   *
   * @example
   * 3.45
   */
  starRating?: number;

  /**
   * Number of reviews for provider
   *
   * @example
   * 15
   */
  reviewCount?: number;

  /**
   * Distance in kilometers from `location`
   *
   * @example
   * 0.43 // 0.43km
   */
  distanceInKm?: number;

  /**
   * Location (point of reference for `distanceInKm`)
   *
   * @example
   * CBD
   */
  location: string;

  isSaviSelectIntl: boolean;

  nation?: string;
}

interface HotelProps extends StateProps {}

/**
 * Format 3 address lines into a comma-separated list.
 *
 * @example
 * formatAddress('123 Alphabet Street', 'Auckland', 'New Zealand')
 * // returns '123 Alphabet Street, Auckland, New Zealand
 *
 * @example
 * formatAddress('123 Alphabet Street', 'New Zealand')
 * // returns '123 Alphabet Street, New Zealand
 */
const formatAddress = (
  /**
   * Address Line 1
   * @example
   * "1 Hobson St Downtown"
   */
  addressLine1?: string,
  /**
   * Address Line 2
   * @example
   * "North Island"
   */
  addressLine2?: string,
  /**
   * Address Line 3
   * @example
   * "New Zealand"
   */
  addressLine3?: string,
) => {
  return commaSeparated([addressLine1, addressLine2, addressLine3].filter(
    Boolean,
  ) as string[]);
};

const mapStateToProps = (
  state: AppState,
  { optionId, segmentId }: OwnProps,
) => {
  if(!isSaviSelectIntl(state)) {
    const {
      addressLine1,
      addressLine2,
      addressLine3,
      distanceInKm,
      starRating,
      location,
    } = getSegmentOption(segmentId, optionId)(state) as HotelSegmentDetails;

    const providerName = commaSeparated(
      getProviderNames(segmentId, optionId)(state),
    );

    return {
      address: formatAddress(addressLine1, addressLine2, addressLine3),
      distanceInKm,
      location,
      providerName,
      reviewCount: undefined,
      starRating,
      isSaviSelectIntl: false,
    };
  } else {
    const targetedHotelSegmentDetail = getAHotelDetailsForSaviSelectIntl(segmentId, Number(optionId))(state);

    return {
      address: formatAddress(targetedHotelSegmentDetail!.hotelAddress1, targetedHotelSegmentDetail!.hotelAddress2),
      nation: formatAddress(targetedHotelSegmentDetail!.hotelAddress3),
      providerName: targetedHotelSegmentDetail!.hotelName,
      starRating: targetedHotelSegmentDetail!.starRating,
      reviewCount: targetedHotelSegmentDetail!.starRating,
      distanceInKm: targetedHotelSegmentDetail!.distanceInKm.toFixed(2),
      location: targetedHotelSegmentDetail!.cityName,
      isSaviSelectIntl: true,
    }
  }
};

const enhance = compose<HotelProps, OwnProps>(connect(mapStateToProps));

/**
 * Hotel provider details
 *
 * @example
 *
 * "Vantis Hotel Group"
 * "1 Hobson St Downtown, Nor..."
 * "📍 0.43km from CB"
 */
export const HotelProviderDetailsContainer = enhance(HotelProviderDetails);

HotelProviderDetailsContainer.displayName = 'HotelProviderDetailsContainer';
